import type { TriggerSourceKey } from 'types/api/runs-common'

export interface FailureFlag {
  apps: string[]
  experiments: string[]
  labelKeys: string[]
  name: string
  protected: boolean
}

export interface ExperimentSeriesData {
  minute: number
  count: number
}

export interface ExperimentRunSummary {
  app: {
    name: string
    labels?: string[] | null
  }
  deliveries: number
  id: string
  impacts: number
  flag: {
    name: string
    labels?: string[] | null
  }
  name: string
  startTime: string
  stopTime: string
  teamId: string
  triggeredBy: string
  triggerSource: TriggerSourceKey
}

export interface ExperimentRun {
  deliverySeries: ExperimentSeriesData[]
  impactSeries: ExperimentSeriesData[]
  startTime: string
  triggeredBy: string
  triggerSource: TriggerSourceKey
}

export interface ExperimentHistoryEntry {
  deliveries: number
  impacts: number
  startTime: string
  stopTime: string
}

export interface LatencyEffect {
  latency: number
}

export interface ExceptionEffect {
  exception: { message: string } | null
}

export type ExperimentEffect =
  | LatencyEffect
  | ExceptionEffect
  | Record<string, unknown>

type FailureFlagSelectorLabels = Record<string, string[]>

// both flag and app selectors have the same type
export interface FailureFlagSelector {
  name: string
  labels: FailureFlagSelectorLabels
}

export type SelectorLabels = FailureFlagSelectorLabels | ExperimentEffect

export type ExperimentDuration = `${number}${'d' | 'm' | 'h'}` | ''

// this is only enforced in the UI, this could be any string on the BE
export type ExperimentRate = 0.01 | 0.05 | 0.1 | 0.25 | 0.5 | 0.75 | 0.9 | 1

export interface FailureFlagExperiment {
  app: FailureFlagSelector
  createdAt: string
  currentRun?: ExperimentRun
  duration: ExperimentDuration
  effect: ExperimentEffect
  flag: FailureFlagSelector
  id: string
  name: string
  protected: boolean
  rate: ExperimentRate
  recentHistory: ExperimentHistoryEntry[]
  startTime: string
  staticTargeting: boolean
  teamId: string
}

export interface ApplicationLabel {
  value: string
  cardinality: number
}

export enum ApplicationType {
  AWSLambda = 'AWS Lambda',
  Container = 'Container'
}

export enum ApplicationLabelTypes {
  Region = 'region',
  Cloud = 'cloud'
}

export interface FailureFlagApplicationLabels {
  [key: string]: ApplicationLabel[]
}

export interface FailureFlagApplication {
  experiments: string[]
  labels: FailureFlagApplicationLabels | Record<string, never> // can be an empty object
  name: string
  protected: boolean
  state: 'Active' | 'Idle'
  type: ApplicationType
}
